import React from 'react';
import { 
  Grid, 
  FormControlLabel, 
  Checkbox, 
  TextField, 
  Autocomplete, 
  Typography, 
  Box, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  ListSubheader,
  FormHelperText,
  Button
} from '@mui/material';
import { 
  imagingTestOptions, 
  preAnaestheticDrugs, 
  surgicalTeamOptions, 
  pendingCommentsOptions, 
  antibioticOptions, 
  dosageOptions, 
  allergyOptions,
  unitsOptions,
  doctorGroups
} from '../constants/options';

const ChecklistSection = ({ formData, handleChange, handleNameChange, setFormData, errors, handleSignatureOpen, handleBlur }) => {
  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" sx={{ color: '#008080', mb: 2, fontWeight: 'bold', borderBottom: '1px solid #ccc' }}>
        Checklist & Procedures
      </Typography>
      <Grid container spacing={3}>
        {/* Row 1: Lab & Imaging */}
        <Grid item xs={12} sm={6}>
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <FormControlLabel
              control={<Checkbox name="imaging_tests_reviewed" checked={formData.imaging_tests_reviewed} onChange={handleChange} />}
              label="Imaging Tests Reviewed"
            />
            {formData.imaging_tests_reviewed && (
              <Autocomplete
                freeSolo
                options={imagingTestOptions}
                value={formData.imaging_tests || ''}
                onInputChange={(event, newInputValue) => {
                  setFormData(prev => ({ ...prev, imaging_tests: newInputValue }));
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Tests" name="imaging_tests" fullWidth size="small" sx={{ mt: 1 }} />
                )}
              />
            )}
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <FormControlLabel
              control={<Checkbox name="all_lab_results_verified" checked={formData.all_lab_results_verified} onChange={handleChange} />}
              label="All Lab Results Verified"
            />
            <FormControlLabel
              control={<Checkbox name="cardiac_clearance" checked={formData.cardiac_clearance} onChange={handleChange} />}
              label="Cardiac Clearance Attached"
            />
          </Box>
        </Grid>

        {/* Row 2: Anesthesia */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth error={!!errors.anaesthesia_type}>
            <InputLabel id="anaesthesia-type-label">
              Anesthesia Type Selected <span style={{ color: 'red' }}>*</span>
            </InputLabel>
            <Select
              labelId="anaesthesia-type-label"
              label="Anesthesia Type Selected *"
              name="anaesthesia_type"
              value={formData.anaesthesia_type || ''}
              onChange={handleChange}
              onBlur={handleBlur}
              size="small"
            >
              <MenuItem value=""><em>Select type</em></MenuItem>
              <MenuItem value="General">General</MenuItem>
              <MenuItem value="Spinal">Spinal</MenuItem>
              <MenuItem value="Local">Local</MenuItem>
              <MenuItem value="Epidural">Epidural</MenuItem>
            </Select>
            <FormHelperText>{errors.anaesthesia_type || ' '}</FormHelperText>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <FormControlLabel
              control={<Checkbox name="pre_anaesthetic_medication_given" checked={formData.pre_anaesthetic_medication_given} onChange={handleChange} />}
              label="Pre-Anesthesia Medication Given"
            />
            {formData.pre_anaesthetic_medication_given && (
              <Autocomplete
                freeSolo
                options={preAnaestheticDrugs}
                value={formData.pre_anaesthetic_medication_details || ''}
                onInputChange={(event, newValue) => {
                  setFormData(prev => ({ ...prev, pre_anaesthetic_medication_details: newValue }));
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Specify Drug & Dosage" name="pre_anaesthetic_medication_details" fullWidth size="small" sx={{ mt: 1 }} />
                )}
              />
            )}
          </Box>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <FormControlLabel
              control={<Checkbox name="patient_sedation_level" checked={formData.patient_sedation_level} onChange={handleChange} />}
              label="Patient Sedation Level Check"
            />
            <FormControlLabel
              control={<Checkbox name="anaesthesiologist_clearance" checked={formData.anaesthesiologist_clearance} onChange={handleChange} />}
              label={
                <span>
                  Anesthesiologist Clearance
                </span>
              }
            />
          </Box>
        </Grid>

        {/* Anesthesiologist Details (Conditional) */}
        {formData.anaesthesiologist_clearance && (
          <Grid item xs={12} sm={6}>
            <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 2, backgroundColor: '#fafafa' }}>
              <FormControl fullWidth error={!!errors.anaesthesiologist_name} size="small">
                <InputLabel id="anaesthesiologist-name-label">
                  Name (Anesthesiologist) <span style={{ color: 'red' }}>*</span>
                </InputLabel>
                <Select
                  labelId="anaesthesiologist-name-label"
                  label="Name (Anesthesiologist) *"
                  name="anaesthesiologist_name"
                  value={formData.anaesthesiologist_name || ""}
                  onChange={handleNameChange}
                  onBlur={handleBlur}
                >
                  <MenuItem value=""><em>Select Doctor</em></MenuItem>
                  {Object.entries(doctorGroups).map(([specialty, doctors]) => [
                    <ListSubheader key={specialty}>{specialty}</ListSubheader>,
                    ...doctors.map((doctor, idx) => (
                      <MenuItem key={idx} value={doctor}>{doctor}</MenuItem>
                    ))
                  ])}
                </Select>
                <FormHelperText>{errors.anaesthesiologist_name || ' '}</FormHelperText>
              </FormControl>
              <Button 
                variant="outlined" 
                size="small" 
                sx={{ mt: 1, color: '#008080', borderColor: '#008080', textTransform: 'none' }}
                onClick={() => handleSignatureOpen('anaesthesiologist_signature')}
              >
                {formData.anaesthesiologist_signature ? 'Update Signature' : 'Sign Here'}
              </Button>
              {formData.anaesthesiologist_signature && (
                <Box sx={{ mt: 1, border: '1px solid #ccc', p: 0.5, borderRadius: 1, backgroundColor: '#fff', maxWidth: '160px' }}>
                  <img src={`data:image/png;base64,${formData.anaesthesiologist_signature}`} alt="Signature" style={{ width: '100%', height: 'auto', display: 'block' }} />
                </Box>
              )}
            </Box>
          </Grid>
        )}

        {/* Surgical Team & Equipment */}
        <Grid item xs={12} sm={6}>
          <TextField
            label="Surgical Team Assigned"
            name="surgical_team_assigned"
            value={formData.surgical_team_assigned || ''}
            onChange={handleNameChange}
            fullWidth
            size="small"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <FormControlLabel
              control={<Checkbox name="surgical_instruments_ready" checked={formData.surgical_instruments_ready} onChange={handleChange} />}
              label="Instruments & Equipment Ready"
            />
            {!formData.surgical_instruments_ready && (
              <Autocomplete
                freeSolo
                options={pendingCommentsOptions}
                value={formData.Comments_if_pending || ''}
                onInputChange={(event, newValue) => {
                  setFormData(prev => ({ ...prev, Comments_if_pending: newValue }));
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Comments if Pending" name="Comments_if_pending" fullWidth size="small" sx={{ mt: 1 }} />
                )}
              />
            )}
          </Box>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={<Checkbox name="ot_sterilization_check" checked={formData.ot_sterilization_check} onChange={handleChange} />}
            label="OT Sterilization Check"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <FormControlLabel
              control={<Checkbox name="blood_products_available" checked={formData.blood_products_available} onChange={handleChange} />}
              label="Blood Products Available"
            />
            {formData.blood_products_available && (
              <Autocomplete
                freeSolo
                options={unitsOptions}
                value={formData.units_reserved || ''}
                onInputChange={(event, newValue) => {
                  setFormData(prev => ({ ...prev, units_reserved: newValue }));
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Units Reserved" name="units_reserved" fullWidth size="small" sx={{ mt: 1 }} />
                )}
              />
            )}
          </Box>
        </Grid>

        {/* Consents Header */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ color: '#008080', fontWeight: 600, mt: 1 }}>
            Required Consents
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <FormControlLabel
              control={<Checkbox name="guardian_consent" checked={formData.guardian_consent} onChange={handleChange} />}
              label="Patient/Guardian Consent Signed"
            />
            <FormControlLabel
              control={<Checkbox name="anaesthesia_consent" checked={formData.anaesthesia_consent} onChange={handleChange} />}
              label="Anesthesia Consent Signed"
            />
            <FormControlLabel
              control={<Checkbox name="blood_transfusion_consent" checked={formData.blood_transfusion_consent} onChange={handleChange} />}
              label="Blood Transfusion Consent Signed"
            />
          </Box>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 2, backgroundColor: '#fefefe' }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: '#666', fontWeight: 700 }}>Legal Representative Details</Typography>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <TextField
                  label="Name"
                  name="legal_representative_name"
                  value={formData.legal_representative_name || ''}
                  onChange={handleNameChange}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={<span>Contact <span style={{ color: '#ff0000' }}>*</span></span>}
                  name="legal_representative_contact"
                  value={formData.legal_representative_contact || ''}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^\d]/g, '');
                    setFormData(prev => ({ ...prev, legal_representative_contact: value }));
                  }}
                  inputProps={{ maxLength: 10, inputMode: 'numeric', pattern: '[0-9]*' }}
                  fullWidth
                  size="small"
                  error={!!errors.legal_representative_contact}
                  helperText={errors.legal_representative_contact || ' '}
                  onBlur={handleBlur}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Consent Time"
                  name="consent_date_time"
                  type="datetime-local"
                  value={formData.consent_date_time || ''}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Medications & Prep */}
        <Grid item xs={12} sm={6}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <FormControlLabel
              control={<Checkbox name="antibiotics_given" checked={formData.antibiotics_given} onChange={handleChange} />}
              label="Antibiotics Given"
            />
            {formData.antibiotics_given && (
              <Box sx={{ mt: 1, p: 2, border: '1px solid #e0e0e0', borderRadius: 2, backgroundColor: '#f9f9f9' }}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Autocomplete
                      freeSolo
                      options={antibioticOptions}
                      value={formData.antibiotics_drug_name || ''}
                      onInputChange={(event, newValue) => {
                        setFormData(prev => ({ ...prev, antibiotics_drug_name: newValue }));
                      }}
                      renderInput={(params) => (
                        <TextField {...params} label="Drug Name" name="antibiotics_drug_name" fullWidth size="small" />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      freeSolo
                      options={dosageOptions}
                      value={formData.antibiotics_dosage || ''}
                      onInputChange={(event, newValue) => {
                        setFormData(prev => ({ ...prev, antibiotics_dosage: newValue }));
                      }}
                      renderInput={(params) => (
                        <TextField {...params} label="Dosage" name="antibiotics_dosage" fullWidth size="small" />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Admin Time"
                      type="datetime-local"
                      name="antibiotics_time"
                      value={formData.antibiotics_time || ''}
                      onChange={handleChange}
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <FormControlLabel
              control={<Checkbox name="anti_coagulants_stopped" checked={formData.anti_coagulants_stopped} onChange={handleChange} />}
              label="Anti-Coagulants Stopped"
            />
            <FormControlLabel
              control={<Checkbox name="skin_prep_completed" checked={formData.skin_prep_completed} onChange={handleChange} />}
              label="Skin Preparation Completed"
            />
            <FormControlLabel
              control={<Checkbox name="pre_surgical_clothing_checked" checked={formData.pre_surgical_clothing_checked} onChange={handleChange} />}
              label="Clothing & ID Band Checked"
            />
          </Box>
        </Grid>

        {/* Blood Type & Matching */}
        <Grid item xs={12} sm={6}>
          <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#666', fontWeight: 700 }}>Blood Type & Compatibility</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Cross-Matching</InputLabel>
                  <Select
                    label="Cross-Matching"
                    name="blood_cross_matching"
                    value={formData.blood_cross_matching || ''}
                    onChange={handleChange}
                  >
                    <MenuItem value="Yes">Yes</MenuItem>
                    <MenuItem value="No">No</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Blood Type</InputLabel>
                  <Select
                    name="blood_type"
                    value={formData.blood_type || ''}
                    onChange={handleChange}
                    label="Blood Type"
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    <MenuItem value="A+">A+</MenuItem>
                    <MenuItem value="A-">A-</MenuItem>
                    <MenuItem value="B+">B+</MenuItem>
                    <MenuItem value="B-">B-</MenuItem>
                    <MenuItem value="AB+">AB+</MenuItem>
                    <MenuItem value="AB-">AB-</MenuItem>
                    <MenuItem value="O+">O+</MenuItem>
                    <MenuItem value="O-">O-</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
             <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#666', fontWeight: 700 }}>Units & Allergies</Typography>
             <Grid container spacing={2}>
               <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Blood Units Reserved</InputLabel>
                  <Select
                    label="Blood Units Reserved"
                    name="blood_units_reserved"
                    value={formData.blood_units_reserved || ''}
                    onChange={handleChange}
                  >
                    <MenuItem value="Yes">Yes</MenuItem>
                    <MenuItem value="No">No</MenuItem>
                  </Select>
                </FormControl>
               </Grid>
               <Grid item xs={12}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <FormControlLabel
                    control={<Checkbox name="allergy_review" checked={formData.allergy_review} onChange={handleChange} />}
                    label="Allergy Review"
                  />
                  {formData.allergy_review && (
                    <Autocomplete
                      freeSolo
                      options={allergyOptions}
                      value={formData.specify_allergies || ''}
                      onInputChange={(event, newValue) => {
                        setFormData(prev => ({ ...prev, specify_allergies: newValue }));
                      }}
                      renderInput={(params) => (
                        <TextField {...params} label="Specify Allergies" name="specify_allergies" fullWidth size="small" sx={{ mt: 1 }} />
                      )}
                    />
                  )}
                </Box>
               </Grid>
             </Grid>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ChecklistSection;
